
/**
 * **워크스페이스 역할 권한을 판단하는 유일한 자리.**
 *
 * 역할은 워크스페이스 문서의 두 필드에서만 나온다.
 *
 * ```
 *   ownerId       오너 uid 하나
 *   managerIdList 매니저 uid 배열 (최대 3, memberList 부분집합, ownerId 제외)
 *   memberList    멤버 uid 배열 (오너·매니저도 포함된다)
 * ```
 *
 * ## 규칙
 *
 * - **오너는 매니저 검사를 거치지 않고 무조건 통과한다.** `canManageWorkspace` 계열은
 *   `isWorkspaceOwner` 를 먼저 보고 빠진다.
 * - **매니저 위임·해임은 오너만.** 매니저는 다른 매니저를 세우지도 내리지도 못한다.
 * - **매니저는 삭제 보호를 받는다.** 매니저를 멤버에서 빼는 것도 오너만 할 수 있다.
 * - **오너 이관은 오너 또는 서비스 관리자(`isAdmin`)만.** 이관된 기존 오너는 일반 멤버가 된다.
 *
 * ## ⚠️ 여기는 화면 게이트일 뿐이다
 *
 * 워크스페이스 쓰기는 전부 클라이언트 SDK 로 나간다. 이 모듈이 false 를 돌려줘도
 * 서버·Firestore 규칙이 막아 주지 않는다. 실제 강제는 규칙이나 서버 라우트에서
 * 따로 세워야 한다 — 이 모듈은 그때 옮겨 쓸 판단 기준을 한자리에 모아 둔 것이다.
 */

/** uid 문자열이거나 `uid`·`id` 중 하나를 가진 user 객체. 화면마다 섞여 있어 둘 다 받는다 */
type WorkspaceUserObject = { uid?: unknown; id?: unknown; isAdmin?: unknown };
type WorkspaceUserRef = string | WorkspaceUserObject | null | undefined;

/** 워크스페이스 문서. 이 모듈이 읽는 것은 ownerId · managerIdList · memberList 뿐이다 */
type WorkspaceDoc = any;

/** 워크스페이스 역할 */
export const WORKSPACE_ROLE = {
  owner: 'owner',
  manager: 'manager',
  member: 'member',
  none: 'none',
};

/** 워크스페이스당 매니저 최대 인원 */
export const MAX_WORKSPACE_MANAGERS = 3;

/** 역할 한글 표기 */
const WORKSPACE_ROLE_LABEL = {
  [WORKSPACE_ROLE.owner]: '오너',
  [WORKSPACE_ROLE.manager]: '매니저',
  [WORKSPACE_ROLE.member]: '멤버',
  [WORKSPACE_ROLE.none]: '',
};

/** 역할 배지 색 (`src/components/label`) */
const WORKSPACE_ROLE_LABEL_COLOR = {
  [WORKSPACE_ROLE.owner]: 'warning',
  [WORKSPACE_ROLE.manager]: 'primary',
  [WORKSPACE_ROLE.member]: 'fail',
  [WORKSPACE_ROLE.none]: 'default',
};

const toId = (value: unknown): string => String(value ?? '').trim();

const toIdList = (value: unknown): string[] =>
  (Array.isArray(value) ? value : []).map(toId).filter(Boolean);

/**
 * uid 문자열 또는 user 객체에서 uid 를 뽑습니다.
 * 화면마다 `user.uid` 와 `user.id` 가 섞여 있어 둘 다 받습니다.
 * @param {Object|string|null|undefined} userOrUid
 * @returns {string}
 */
export function resolveUserId(userOrUid: WorkspaceUserRef): string {
  if (!userOrUid) return '';
  if (typeof userOrUid === 'string') return toId(userOrUid);
  return toId(userOrUid.uid) || toId(userOrUid.id);
}

/**
 * 오너 uid
 * @param {Object|null|undefined} workspace
 * @returns {string}
 */
function getWorkspaceOwnerId(workspace: WorkspaceDoc) {
  return toId(workspace?.ownerId);
}

/**
 * 매니저 uid 목록. 오너 중복·빈값을 걷어 낸 결과를 돌려줍니다.
 * @param {Object|null|undefined} workspace
 * @returns {string[]}
 */
export function getWorkspaceManagerIds(workspace: WorkspaceDoc) {
  const ownerId = getWorkspaceOwnerId(workspace);
  const ids = toIdList(workspace?.managerIdList).filter((id) => id !== ownerId);
  return Array.from(new Set(ids));
}

/**
 * 멤버 uid 목록. 오너는 memberList 에 없더라도 포함시킵니다.
 * @param {Object|null|undefined} workspace
 * @returns {string[]}
 */
export function getWorkspaceMemberIds(workspace: WorkspaceDoc) {
  const ownerId = getWorkspaceOwnerId(workspace);
  return Array.from(new Set([ownerId, ...toIdList(workspace?.memberList)].filter(Boolean)));
}

/**
 * 해당 사용자의 역할
 * @param {Object|string|null|undefined} userOrUid
 * @param {Object|null|undefined} workspace
 * @returns {string} WORKSPACE_ROLE 값
 */
export function getWorkspaceRole(userOrUid: WorkspaceUserRef, workspace: WorkspaceDoc) {
  const uid = resolveUserId(userOrUid);
  if (!uid || !workspace) return WORKSPACE_ROLE.none;

  if (uid === getWorkspaceOwnerId(workspace)) return WORKSPACE_ROLE.owner;
  if (getWorkspaceManagerIds(workspace).includes(uid)) return WORKSPACE_ROLE.manager;
  if (getWorkspaceMemberIds(workspace).includes(uid)) return WORKSPACE_ROLE.member;

  return WORKSPACE_ROLE.none;
}

/**
 * 오너인가
 * @param {Object|string|null|undefined} userOrUid
 * @param {Object|null|undefined} workspace
 * @returns {boolean}
 */
function isWorkspaceOwner(userOrUid: WorkspaceUserRef, workspace: WorkspaceDoc) {
  return getWorkspaceRole(userOrUid, workspace) === WORKSPACE_ROLE.owner;
}

/**
 * 매니저인가 (오너는 제외 — 오너 포함 판정은 `canManageWorkspace`)
 * @param {Object|string|null|undefined} userOrUid
 * @param {Object|null|undefined} workspace
 * @returns {boolean}
 */
function isWorkspaceManager(userOrUid: WorkspaceUserRef, workspace: WorkspaceDoc) {
  return getWorkspaceRole(userOrUid, workspace) === WORKSPACE_ROLE.manager;
}

/**
 * 워크스페이스 멤버인가 (오너·매니저 포함)
 * @param {Object|string|null|undefined} userOrUid
 * @param {Object|null|undefined} workspace
 * @returns {boolean}
 */
export function isWorkspaceMember(userOrUid: WorkspaceUserRef, workspace: WorkspaceDoc) {
  return getWorkspaceRole(userOrUid, workspace) !== WORKSPACE_ROLE.none;
}

/**
 * 워크스페이스 관리 권한(오너 또는 매니저)
 * @param {Object|string|null|undefined} userOrUid
 * @param {Object|null|undefined} workspace
 * @returns {boolean}
 */
export function canManageWorkspace(userOrUid: WorkspaceUserRef, workspace: WorkspaceDoc) {
  const role = getWorkspaceRole(userOrUid, workspace);
  return role === WORKSPACE_ROLE.owner || role === WORKSPACE_ROLE.manager;
}

/**
 * 매니저 위임·해임 권한 — 오너만.
 * @param {Object|string|null|undefined} userOrUid
 * @param {Object|null|undefined} workspace
 * @returns {boolean}
 */
export function canAssignManager(userOrUid: WorkspaceUserRef, workspace: WorkspaceDoc) {
  return isWorkspaceOwner(userOrUid, workspace);
}

/**
 * 매니저 자리가 남았는가
 * @param {Object|null|undefined} workspace
 * @returns {boolean}
 */
export function hasAvailableManagerSlot(workspace: WorkspaceDoc) {
  return getWorkspaceManagerIds(workspace).length < MAX_WORKSPACE_MANAGERS;
}

/**
 * 대상을 매니저로 세울 수 있는가.
 * 오너 자신·이미 매니저인 사람·멤버가 아닌 사람은 대상이 아니고, 자리도 남아 있어야 합니다.
 * @param {Object|string|null|undefined} actor 실행하는 사람
 * @param {Object|string|null|undefined} target 대상
 * @param {Object|null|undefined} workspace
 * @returns {boolean}
 */
export function canAssignManagerTo(actor: WorkspaceUserRef, target: WorkspaceUserRef, workspace: WorkspaceDoc) {
  if (!canAssignManager(actor, workspace)) return false;

  const targetRole = getWorkspaceRole(target, workspace);
  if (targetRole !== WORKSPACE_ROLE.member) return false;

  return hasAvailableManagerSlot(workspace);
}

/**
 * 대상의 매니저를 해임할 수 있는가 — 오너만, 대상이 매니저일 때.
 * @param {Object|string|null|undefined} actor
 * @param {Object|string|null|undefined} target
 * @param {Object|null|undefined} workspace
 * @returns {boolean}
 */
export function canUnassignManagerFrom(actor: WorkspaceUserRef, target: WorkspaceUserRef, workspace: WorkspaceDoc) {
  if (!canAssignManager(actor, workspace)) return false;
  return isWorkspaceManager(target, workspace);
}

/**
 * 대상을 멤버에서 뺄 수 있는가.
 *
 * - 오너는 자기 자신을 뺄 수 없다 (이관이 먼저다)
 * - **매니저는 삭제 보호를 받는다** — 매니저를 빼는 것은 오너만
 * - 매니저는 일반 멤버만 뺄 수 있다
 *
 * @param {Object|string|null|undefined} actor
 * @param {Object|string|null|undefined} target
 * @param {Object|null|undefined} workspace
 * @returns {boolean}
 */
export function canRemoveMember(actor: WorkspaceUserRef, target: WorkspaceUserRef, workspace: WorkspaceDoc) {
  const actorRole = getWorkspaceRole(actor, workspace);
  const targetRole = getWorkspaceRole(target, workspace);

  if (targetRole === WORKSPACE_ROLE.owner || targetRole === WORKSPACE_ROLE.none) return false;

  if (actorRole === WORKSPACE_ROLE.owner) return true;
  if (actorRole === WORKSPACE_ROLE.manager) return targetRole === WORKSPACE_ROLE.member;

  return false;
}

/**
 * 오너 이관 권한 — 오너 또는 서비스 관리자(`isAdmin`).
 * `isAdmin` 을 봐야 해서 uid 문자열이 아니라 user 객체가 필요합니다.
 * @param {Object|null|undefined} user
 * @param {Object|null|undefined} workspace
 * @returns {boolean}
 */
function canTransferOwnership(user: WorkspaceUserObject | null | undefined, workspace: WorkspaceDoc) {
  if (user?.isAdmin === true) return true;
  return isWorkspaceOwner(user, workspace);
}

/**
 * 대상에게 오너를 넘길 수 있는가 — 대상이 오너가 아닌 멤버여야 합니다.
 * @param {Object|null|undefined} user
 * @param {Object|string|null|undefined} target
 * @param {Object|null|undefined} workspace
 * @returns {boolean}
 */
export function canTransferOwnershipTo(user: WorkspaceUserObject | null | undefined, target: WorkspaceUserRef, workspace: WorkspaceDoc) {
  if (!canTransferOwnership(user, workspace)) return false;

  const targetRole = getWorkspaceRole(target, workspace);
  return targetRole === WORKSPACE_ROLE.manager || targetRole === WORKSPACE_ROLE.member;
}

/**
 * 역할 배지에 쓸 표기와 색
 * @param {Object|string|null|undefined} userOrUid
 * @param {Object|null|undefined} workspace
 * @returns {{ role: string, label: string, color: string }}
 */
export function getWorkspaceRoleBadge(userOrUid: WorkspaceUserRef, workspace: WorkspaceDoc) {
  const role = getWorkspaceRole(userOrUid, workspace);

  // 멤버가 아닌 워크스페이스는 `isAdmin` 만 목록에서 만난다(`getWorkspaceList` 가
  // 관리자에게는 전체를 준다). 역할 구분이 생기기 전에는 오너가 아니면 전부 '멤버'로
  // 찍었고 그 표기를 그대로 둔다 — 빈 칩이 남는 것보다 낫다.
  // `role` 은 진짜 값(`none`)을 그대로 돌려주므로 판단이 필요한 쪽은 그것을 본다.
  const display = role === WORKSPACE_ROLE.none ? WORKSPACE_ROLE.member : role;

  return {
    role,
    label: WORKSPACE_ROLE_LABEL[display],
    color: WORKSPACE_ROLE_LABEL_COLOR[display],
  };
}
